import { formatDate } from '@angular/common';

export type CreditKind = 'entity' | 'employee';

export type CreditOperation = 'constitution' | 'shortTermDebt' | 'installments';

export interface CreditDetailAction {
  type: CreditOperation | 'edit' | 'changed';
  editUid?: string;
}

export interface CreditDialogData {
  kind: CreditKind;
  credit: any;
  editUid?: string;
}

export const CREDIT_TERM_LABELS: Record<number, string> = {
  1: 'Corto plazo',
  2: 'Largo plazo',
};

export const CREDIT_SETTLEMENT_LABELS: Record<number, string> = {
  1: 'Mensual',
  2: 'Bimensual',
  3: 'Trimestral',
  4: 'Semestral',
  5: 'Anual',
};

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export interface CreditListFilter {
  page: number;
  pageSize: number;
  search: string;
  sortField: string;
  sortDirection: string;
}

export interface CreditListSummary {
  initialAmount: number;
  outstandingAmount: number;
}

export interface CreditListResult {
  items: any[];
  total: number;
  summary: CreditListSummary;
}

export const EMPTY_CREDIT_LIST_SUMMARY: CreditListSummary = { initialAmount: 0, outstandingAmount: 0 };

export function roundMoney(value: number): number {
  return Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;
}

export function creditTitle(kind: CreditKind, credit: any): string {
  if (!credit) return '';
  return kind === 'employee'
    ? (credit.employeesName || 'Crédito con empleado')
    : (credit.descripcion || 'Crédito');
}

export function creditCollected(credit: any): number {
  if (!credit) return 0;
  return roundMoney(Math.max(0, (Number(credit.importeInicial) || 0) - (Number(credit.importePendiente) || 0)));
}

export function creditPercent(credit: any): number {
  const initial = Number(credit && credit.importeInicial) || 0;
  if (initial <= 0) return 0;
  return Math.min(100, Math.max(0, (creditCollected(credit) / initial) * 100));
}

export function creditStatus(credit: any): { label: string; variant: 'success' | 'info' | 'warning' | 'neutral' } {
  const initial = Number(credit && credit.importeInicial) || 0;
  const pending = Number(credit && credit.importePendiente) || 0;
  if (initial <= 0) return { label: 'Sin importe', variant: 'neutral' };
  if (pending <= 0) return { label: 'Cobrado', variant: 'success' };
  if (pending < initial) return { label: 'En curso', variant: 'info' };
  return { label: 'Pendiente', variant: 'warning' };
}

export function hasAttachment(kind: CreditKind, credit: any): boolean {
  const uid = kind === 'employee' ? credit && credit.filesCreditosConEmpleadosUid : credit && credit.filesCreditosUid;
  return !!uid && uid !== EMPTY_GUID;
}

export function toApiDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return formatDate(date, 'yyyy-MM-dd', 'es-ES');
}

export function fromApiDate(value: any): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

export function displayDate(value: any): string {
  const date = fromApiDate(value);
  return date ? formatDate(date, 'dd/MM/yyyy', 'es-ES') : '';
}

export function stripConceptPrefix(concept: string, prefix: string): string {
  let text = concept || '';
  const head = (prefix || '') + ' - ';
  if (!prefix) {
    return text.startsWith(head) ? text.substring(head.length) : text;
  }
  while (text.startsWith(head)) {
    text = text.substring(head.length);
  }
  return text;
}

export function conceptPrefix(kind: CreditKind, credit: any): string {
  if (!credit) return '';
  return kind === 'employee' ? (credit.employeesName || '') : (credit.descripcion || '');
}

export function sortByDateDesc<T>(items: T[], field: keyof T): T[] {
  return [...items].sort((a, b) => {
    const da = fromApiDate(a[field]);
    const db = fromApiDate(b[field]);
    return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
  });
}
